<template>
  <div class="task-exam-analysis-page">
    <CommonHeader />

    <main :class="['analysis-page-main', { 'analysis-page-main--ready': pageReady, 'analysis-page-main--loading': pageLoading || !pageReady }]">
      <section v-if="pageLoading || !pageReady" class="analysis-loading-wrap panel-card">
        <div class="analysis-loading-wrap__glow"></div>
        <div class="analysis-loading-wrap__icon">
          <span class="analysis-loading-wrap__ring"></span>
          <i class="el-icon-loading analysis-loading-wrap__symbol"></i>
        </div>
        <div class="analysis-loading-wrap__title">解析数据加载中</div>
        <div class="analysis-loading-wrap__desc">正在整理答题结果与解析内容，请稍候...</div>
        <div class="analysis-loading-skeleton">
          <span class="analysis-loading-skeleton__block analysis-loading-skeleton__block--lg"></span>
          <span class="analysis-loading-skeleton__block"></span>
          <span class="analysis-loading-skeleton__block"></span>
          <span class="analysis-loading-skeleton__block analysis-loading-skeleton__block--sm"></span>
        </div>
      </section>

      <div v-else class="analysis-page-container">
        <aside class="analysis-sidebar panel-card">
          <div class="analysis-sidebar__title">
            <i class="el-icon-menu"></i>
            <span>题目列表</span>
          </div>

          <div v-if="questionList.length" class="analysis-nav-grid">
            <button
              v-for="(item, index) in questionList"
              :key="item.id || index"
              type="button"
              :class="['analysis-nav-item', navItemClass(item, index)]"
              @click="goQuestion(index)"
            >
              {{ displayQuestionNumber(item, index) }}
            </button>
          </div>
          <el-empty v-else description="暂无题目" :image-size="88"></el-empty>

          <div class="analysis-legend">
            <div class="analysis-legend__item"><span class="analysis-legend__dot analysis-legend__dot--right"></span><span>答对</span></div>
            <div class="analysis-legend__item"><span class="analysis-legend__dot analysis-legend__dot--wrong"></span><span>答错</span></div>
            <div class="analysis-legend__item"><span class="analysis-legend__dot analysis-legend__dot--pending"></span><span>待阅卷</span></div>
            <div class="analysis-legend__item"><span class="analysis-legend__dot analysis-legend__dot--current"></span><span>当前查看</span></div>
          </div>
        </aside>

        <section class="analysis-main panel-card">
          <template v-if="activeQuestion">
            <div class="analysis-main__head">
              <div class="analysis-main__index">题目 {{ currentIndex + 1 }}/{{ questionList.length }}</div>
              <el-tag v-if="isPendingReview(activeQuestion)" size="mini" type="warning" effect="plain">待阅卷</el-tag>
              <el-tag v-else-if="isQuestionWrong(activeQuestion)" size="mini" type="danger" effect="plain">错题</el-tag>
              <el-tag v-else size="mini" type="success" effect="plain">答对</el-tag>
            </div>

            <div class="analysis-question-title">{{ displayQuestionNumber(activeQuestion, currentIndex) }}. {{ activeQuestion.title }}</div>

            <div v-if="Array.isArray(activeQuestion.content) && activeQuestion.content.length" class="analysis-option-list">
              <div
                v-for="(option, optionIndex) in activeQuestion.content"
                :key="optionIndex"
                :class="optionItemClass(activeQuestion, option.value)"
              >
                <div :class="['analysis-option-item__badge', optionBadgeClass(activeQuestion, option.value)]">{{ option.value }}</div>
                <div class="analysis-option-item__text">{{ option.option }}</div>
                <i v-if="isCorrectOption(activeQuestion, option.value)" class="el-icon-circle-check analysis-option-item__icon analysis-option-item__icon--correct"></i>
                <i v-else-if="isUserSelected(activeQuestion, option.value)" class="el-icon-close analysis-option-item__icon analysis-option-item__icon--wrong"></i>
              </div>
            </div>

            <div v-else class="analysis-answer-box-group">
              <div class="analysis-answer-box analysis-answer-box--mine">
                <div class="analysis-answer-box__label">我的答案</div>
                <div class="analysis-answer-box__value">{{ formatSolution(activeQuestion.solution, activeQuestion) }}</div>
              </div>
              <div class="analysis-answer-box analysis-answer-box--correct">
                <div class="analysis-answer-box__label">正确答案</div>
                <div class="analysis-answer-box__value">{{ formatCorrectAnswer(activeQuestion.answer, activeQuestion) }}</div>
              </div>
            </div>

            <div v-if="isPendingReview(activeQuestion)" class="analysis-pending-tip">
              该题为人工阅卷题，当前仍待管理员阅卷，当前成绩页展示的是已判分题目的得分。
            </div>

            <div class="analysis-expert-card">
              <div class="analysis-expert-card__title">
                <i class="el-icon-question"></i>
                <span>专家解析</span>
              </div>
              <div class="analysis-expert-card__body">
                {{ activeQuestion.analysis || activeQuestion.analyze || activeQuestion.resolve || buildDefaultAnalysis(activeQuestion) }}
              </div>
              <div class="analysis-expert-card__foot">
                相关知识点：{{ test.name || '考试重点内容' }}
              </div>
            </div>

            <div class="analysis-actions">
              <div class="analysis-actions__left">
                <el-button round plain :disabled="currentIndex <= 0" @click="goPrev">上一题</el-button>
                <el-button round plain type="primary" :disabled="currentIndex >= questionList.length - 1" @click="goNext">下一题</el-button>
              </div>
              <el-button round type="primary" icon="el-icon-s-home" @click="goResultPage">返回成绩单</el-button>
            </div>
          </template>

          <el-empty v-else description="暂无解析内容" :image-size="96"></el-empty>
        </section>
      </div>
    </main>

    <footer class="analysis-page-footer">
      <div class="analysis-page-footer__inner">
        <div class="analysis-page-footer__left">© 2024 智学云平台。保留所有权利。</div>
        <div class="analysis-page-footer__links">
          <a href="javascript:void(0)">帮助中心</a>
          <a href="javascript:void(0)">服务条款</a>
          <a href="javascript:void(0)">隐私政策</a>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import CommonHeader from '@/components/header/CommonHeader.vue'
import { getApeTestResult } from '../../api/api'

export default {
  name: 'TaskExamAnalysis',
  components: {
    CommonHeader
  },
  data() {
    return {
      id: '',
      assign: [],
      test: {},
      currentIndex: 0,
      pageLoading: true,
      pageReady: false
    }
  },
  computed: {
    pageTitle() {
      const taskName = this.test.taskName ? this.test.taskName + ' · ' : ''
      return taskName + (this.test.name || '错题解析')
    },
    wrongQuestions() {
      return this.assign.filter(item => this.isQuestionWrong(item))
    },
    questionList() {
      return this.assign
    },
    activeQuestion() {
      return this.questionList[this.currentIndex] || null
    }
  },
  methods: {
    goTaskList() {
      this.$router.push('/center/tasks')
    },
    activatePage() {
      this.$nextTick(() => {
        window.requestAnimationFrame(() => {
          this.pageReady = true
        })
      })
    },
    goResultPage() {
      this.$router.push({ path: '/testResult', query: { id: this.id, fromTask: '1' } })
    },
    goQuestion(index) {
      this.currentIndex = index
      this.scrollToTop()
    },
    goPrev() {
      if (this.currentIndex > 0) {
        this.currentIndex -= 1
        this.scrollToTop()
      }
    },
    goNext() {
      if (this.currentIndex < this.questionList.length - 1) {
        this.currentIndex += 1
        this.scrollToTop()
      }
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    displayQuestionNumber(item, index) {
      return item && item.sort ? item.sort : index + 1
    },
    questionTypeLabel(type) {
      const typeMap = {
        0: '单选题',
        1: '多选题',
        2: '填空题',
        3: '判断题',
        4: '问答题',
        5: '主观题'
      }
      return typeMap[String(type)] || '试题'
    },
    questionTypeClass(type) {
      const typeMap = {
        0: 'single',
        1: 'multiple',
        2: 'blank',
        3: 'judge',
        4: 'qa',
        5: 'essay'
      }
      return typeMap[String(type)] || 'default'
    },
    normalizeAnswer(value) {
      if (Array.isArray(value)) {
        return value.map(item => String(item).trim()).filter(Boolean).sort()
      }
      if (value === null || value === undefined || String(value).trim() === '') {
        return []
      }
      return String(value)
        .split(',')
        .map(item => item.trim())
        .filter(Boolean)
        .sort()
    },
    isPendingReview(item) {
      return item && item.reviewStatus === '待阅卷'
    },
    isQuestionWrong(item) {
      if (this.isPendingReview(item)) {
        return false
      }
      return Number(item.score || 0) !== Number(item.point || 0)
    },
    isCorrectOption(item, optionValue) {
      return this.normalizeAnswer(item.answer).includes(String(optionValue))
    },
    isUserSelected(item, optionValue) {
      return this.normalizeAnswer(item.solution).includes(String(optionValue))
    },
    navItemClass(item, index) {
      if (index === this.currentIndex) {
        return 'analysis-nav-item--current'
      }
      if (this.isPendingReview(item)) {
        return 'analysis-nav-item--pending'
      }
      return this.isQuestionWrong(item) ? 'analysis-nav-item--wrong' : 'analysis-nav-item--right'
    },
    optionItemClass(item, optionValue) {
      return [
        'analysis-option-item',
        {
          'analysis-option-item--correct': this.isCorrectOption(item, optionValue),
          'analysis-option-item--wrong': this.isUserSelected(item, optionValue) && !this.isCorrectOption(item, optionValue),
          'analysis-option-item--plain': !this.isCorrectOption(item, optionValue) && !this.isUserSelected(item, optionValue)
        }
      ]
    },
    optionBadgeClass(item, optionValue) {
      if (this.isCorrectOption(item, optionValue)) {
        return 'analysis-option-item__badge--correct'
      }
      if (this.isUserSelected(item, optionValue) && !this.isCorrectOption(item, optionValue)) {
        return 'analysis-option-item__badge--wrong'
      }
      return 'analysis-option-item__badge--plain'
    },
    getOptionTextMap(item) {
      const map = {}
      if (item && Array.isArray(item.content)) {
        item.content.forEach(option => {
          if (option && option.value !== undefined) {
            map[String(option.value)] = option.option
          }
        })
      }
      return map
    },
    formatSolution(solution, item) {
      const optionTextMap = this.getOptionTextMap(item)
      if (Array.isArray(solution)) {
        if (!solution.length) return '未作答'
        return solution.map(code => optionTextMap[String(code)] ? `${code}. ${optionTextMap[String(code)]}` : code).join('、')
      }
      if (solution === null || solution === undefined || String(solution).trim() === '') {
        return '未作答'
      }
      const text = String(solution).trim()
      return optionTextMap[text] ? `${text}. ${optionTextMap[text]}` : text
    },
    formatCorrectAnswer(answer, item) {
      const optionTextMap = this.getOptionTextMap(item)
      if (answer === null || answer === undefined || String(answer).trim() === '') {
        return '暂无'
      }
      return String(answer)
        .split(',')
        .map(part => part.trim())
        .filter(Boolean)
        .map(part => optionTextMap[part] ? `${part}. ${optionTextMap[part]}` : part)
        .join('、')
    },
    buildDefaultAnalysis(item) {
      const myAnswer = this.formatSolution(item.solution, item)
      const correctAnswer = this.formatCorrectAnswer(item.answer, item)
      const remark = item && item.remark ? String(item.remark).trim() : ''
      return remark
        ? `本题正确答案为 ${correctAnswer}，您的作答为 ${myAnswer}。\n${remark}`
        : `本题正确答案为 ${correctAnswer}，您的作答为 ${myAnswer}。`
    },
    getApeTestResult() {
      this.pageLoading = true
      this.pageReady = false
      if (!this.id) {
        this.pageLoading = false
        this.activatePage()
        return
      }
      getApeTestResult({ id: this.id }).then(res => {
        if (res.code === 1000) {
          this.assign = res.data.testItem || []
          this.test = res.data.test || {}
          this.assign.forEach(item => {
            if (String(item.type) === '0' || String(item.type) === '1') {
              if (item.content) {
                item.content = JSON.parse(item.content)
              }
              if (String(item.type) === '1') {
                item.solution = item.solution ? item.solution.split(',') : []
              }
            }
          })
          const firstWrongIndex = this.assign.findIndex(item => this.isQuestionWrong(item))
          this.currentIndex = firstWrongIndex >= 0 ? firstWrongIndex : 0
        }
      }).catch(() => {
        this.$message.error('错题解析加载失败')
      }).finally(() => {
        this.pageLoading = false
        this.activatePage()
      })
    }
  },
  mounted() {
    this.id = this.$route.query.id || ''
    this.getApeTestResult()
    this.scrollToTop()
  }
}
</script>

<style scoped>
.task-exam-analysis-page {
  min-height: 100vh;
  background: #eef1f5;
  display: flex;
  flex-direction: column;
}

.analysis-page-main {
  flex: 1;
  padding: 22px 24px 40px;
}

.analysis-page-main--loading {
  display: flex;
}

.analysis-loading-wrap {
  position: relative;
  overflow: hidden;
  flex: 1;
  width: min(100%, 1280px);
  margin: 0 auto;
  min-height: calc(100vh - 190px);
  padding: 48px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  border: 1px solid #dbe7ff;
  background:
    radial-gradient(circle at top, rgba(61, 115, 245, 0.14), transparent 34%),
    linear-gradient(180deg, #ffffff 0%, #f6f9ff 100%);
}

.analysis-loading-wrap__glow {
  position: absolute;
  inset: auto auto -120px -80px;
  width: 320px;
  height: 320px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(61, 115, 245, 0.18) 0%, rgba(61, 115, 245, 0) 72%);
  pointer-events: none;
}

.analysis-loading-wrap__icon {
  width: 88px;
  height: 88px;
  position: relative;
  border-radius: 50%;
  background: linear-gradient(180deg, #eef4ff 0%, #e0ecff 100%);
  color: #3d73f5;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  box-shadow: inset 0 0 0 1px rgba(61, 115, 245, 0.08);
}

.analysis-loading-wrap__ring {
  position: absolute;
  inset: -10px;
  border-radius: 50%;
  border: 3px solid rgba(61, 115, 245, 0.12);
  border-top-color: rgba(61, 115, 245, 0.82);
  border-right-color: rgba(61, 115, 245, 0.42);
  animation: analysis-loading-rotate 1.1s linear infinite;
}

.analysis-loading-wrap__symbol {
  position: relative;
  z-index: 1;
  animation: analysis-loading-breathe 1.6s ease-in-out infinite;
}

.analysis-loading-wrap__title {
  margin-top: 20px;
  font-size: 28px;
  font-weight: 700;
  color: #1f2d3d;
}

.analysis-loading-wrap__desc {
  margin-top: 10px;
  color: #7f8a9c;
  font-size: 14px;
}

.analysis-loading-skeleton {
  width: min(640px, 100%);
  margin-top: 28px;
  display: grid;
  gap: 12px;
  position: relative;
  z-index: 1;
}

.analysis-loading-skeleton__block {
  display: block;
  height: 16px;
  border-radius: 999px;
  background: linear-gradient(90deg, #eef2f7 20%, #f8fbff 45%, #eef2f7 70%);
  background-size: 200% 100%;
  animation: analysis-loading-shimmer 1.35s ease-in-out infinite;
}

.analysis-loading-skeleton__block--lg {
  height: 20px;
  width: 82%;
  justify-self: center;
}

.analysis-loading-skeleton__block--sm {
  width: 56%;
  justify-self: center;
}

@keyframes analysis-loading-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@keyframes analysis-loading-rotate {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes analysis-loading-breathe {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(0.92); opacity: 0.78; }
}

.analysis-page-container {
  width: min(100%, 1280px);
  margin: 0 auto;
  display: grid;
  grid-template-columns: 168px minmax(0, 1fr);
  gap: 18px;
  align-items: start;
}

.analysis-sidebar,
.analysis-main {
  opacity: 0;
  transform: translateY(18px);
  transition: opacity 0.35s ease, transform 0.35s ease;
}

.analysis-page-main--ready .analysis-sidebar,
.analysis-page-main--ready .analysis-main {
  opacity: 1;
  transform: translateY(0);
}

.analysis-page-main--ready .analysis-main {
  transition-delay: 0.06s;
}

.panel-card {
  background: #fff;
  border-radius: 22px;
  box-shadow: 0 14px 36px rgba(15, 23, 42, 0.08);
}

.analysis-sidebar {
  padding: 18px 14px 16px;
  position: sticky;
  top: 18px;
}

.analysis-sidebar__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
  color: #1f2d3d;
}

.analysis-nav-grid {
  margin-top: 14px;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 8px;
}

.analysis-nav-item {
  height: 34px;
  border: 0;
  border-radius: 10px;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.analysis-nav-item:hover {
  transform: translateY(-1px);
}

.analysis-nav-item--right {
  background: #2fb978;
}

.analysis-nav-item--wrong {
  background: #ef5350;
}

.analysis-nav-item--pending {
  background: #f59e0b;
}

.analysis-nav-item--current {
  background: #3d73f5;
  box-shadow: 0 0 0 2px #ffffff, 0 0 0 4px rgba(61, 115, 245, 0.35);
}

.analysis-legend {
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid #eef1f5;
  display: grid;
  gap: 10px;
}

.analysis-legend__item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #7f8a9c;
  font-size: 12px;
}

.analysis-legend__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.analysis-legend__dot--right {
  background: #2fb978;
}

.analysis-legend__dot--wrong {
  background: #ef5350;
}

.analysis-legend__dot--pending {
  background: #f59e0b;
}

.analysis-legend__dot--current {
  background: #3d73f5;
}

.analysis-main {
  padding: 22px 22px 20px;
}

.analysis-main__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.analysis-main__index {
  font-size: 13px;
  color: #7f8a9c;
  font-weight: 600;
}

.analysis-question-title {
  margin-top: 18px;
  font-size: 18px;
  line-height: 1.8;
  color: #1f2d3d;
  font-weight: 700;
}

.analysis-option-list {
  margin-top: 24px;
  display: grid;
  gap: 12px;
}

.analysis-option-item {
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) 20px;
  gap: 14px;
  align-items: center;
  min-height: 56px;
  border-radius: 14px;
  padding: 0 16px;
  border: 1px solid #e7ecf3;
  background: #fbfcfd;
}

.analysis-option-item--plain {
  background: #fbfcfd;
  border-color: #e7ecf3;
}

.analysis-option-item--wrong {
  background: #fff4f4;
  border-color: #f4b8b8;
}

.analysis-option-item--correct {
  background: #eefaf2;
  border-color: #b7e5c3;
}

.analysis-option-item__badge {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
}

.analysis-option-item__badge--plain {
  background: #fff;
  border: 1px solid #dfe5ee;
  color: #666;
}

.analysis-option-item__badge--wrong {
  background: #ef5350;
  color: #fff;
}

.analysis-option-item__badge--correct {
  background: #2fb978;
  color: #fff;
}

.analysis-option-item__text {
  font-size: 15px;
  color: #364152;
  line-height: 1.7;
}

.analysis-option-item__icon {
  font-size: 18px;
}

.analysis-option-item__icon--correct {
  color: #2fb978;
}

.analysis-option-item__icon--wrong {
  color: #ef5350;
}

.analysis-answer-box-group {
  margin-top: 22px;
  display: grid;
  gap: 12px;
}

.analysis-answer-box {
  border-radius: 14px;
  padding: 16px 18px;
  border: 1px solid #e7ecf3;
}

.analysis-answer-box--mine {
  background: #fff4f4;
  border-color: #f4b8b8;
}

.analysis-answer-box--correct {
  background: #eefaf2;
  border-color: #b7e5c3;
}

.analysis-answer-box__label {
  font-size: 13px;
  color: #7f8a9c;
  font-weight: 600;
}

.analysis-answer-box__value {
  margin-top: 8px;
  font-size: 14px;
  line-height: 1.8;
  color: #1f2d3d;
}

.analysis-pending-tip {
  margin-top: 18px;
  padding: 14px 16px;
  border-radius: 14px;
  background: #fff8eb;
  border: 1px solid #f6d490;
  color: #946200;
  font-size: 13px;
  line-height: 1.8;
}

.analysis-expert-card {
  margin-top: 26px;
  border-radius: 14px;
  border: 1px solid #d8e4fb;
  background: #f6f9ff;
  padding: 18px 20px;
}

.analysis-expert-card__title {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #3d73f5;
  font-size: 16px;
  font-weight: 700;
}

.analysis-expert-card__body {
  margin-top: 14px;
  color: #5d6880;
  font-size: 14px;
  line-height: 1.9;
  white-space: pre-line;
}

.analysis-expert-card__foot {
  margin-top: 12px;
  color: #4e78df;
  font-size: 12px;
}

.analysis-actions {
  margin-top: 30px;
  padding-top: 22px;
  border-top: 1px solid #eef1f5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.analysis-actions__left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.analysis-page-footer {
  border-top: 1px solid #e5e7eb;
  background: #fff;
}

.analysis-page-footer__inner {
  width: min(100%, 1200px);
  margin: 0 auto;
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  color: #7b8794;
  font-size: 13px;
}

.analysis-page-footer__links {
  display: flex;
  align-items: center;
  gap: 22px;
}

.analysis-page-footer__links a {
  color: #7b8794;
  text-decoration: none;
}

.analysis-page-footer__links a:hover {
  color: #3d73f5;
}

@media (max-width: 960px) {
  .analysis-page-container {
    grid-template-columns: 1fr;
  }

  .analysis-sidebar {
    position: static;
  }
}

@media (max-width: 768px) {
  .analysis-page-main {
    padding: 18px 16px 32px;
  }

  .analysis-loading-wrap {
    min-height: 420px;
    padding: 36px 20px;
  }

  .analysis-loading-wrap__title {
    font-size: 24px;
  }

  .analysis-nav-grid {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }

  .analysis-main {
    padding: 18px;
  }

  .analysis-option-item {
    grid-template-columns: 36px minmax(0, 1fr) 18px;
    padding: 12px 14px;
  }

  .analysis-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .analysis-actions__left {
    width: 100%;
  }

  .analysis-actions__left /deep/ .el-button,
  .analysis-actions /deep/ .el-button {
    width: 100%;
    margin-left: 0;
  }

  .analysis-page-footer__inner {
    flex-direction: column;
  }
}
</style>
